/**
 * AP-32 11.3 — **Der Layout-Konfigurator.**
 *
 * Links das Formular in Abschnitten (Seite · Typografie · Blöcke · Tabelle ·
 * Farbe), rechts die **Vorschau des PDF** mit dem Musterbeleg, entprellt neu
 * gerendert nach jeder Änderung. Millimeterfelder statt Leinwand (19.1-8): Ein
 * Zahlenfeld lässt sich zurücksetzen, ein verschobener Kasten nicht.
 *
 * Die Prüfung sitzt im Server (dieselbe für Vorschau und Speichern); die Maske
 * zeigt ihre Meldungen am Feld.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowLeft, ArrowUp, Copy, RotateCcw, Save, Trash2 } from 'lucide-react'
import { DEFAULT_DOCUMENT_LAYOUT, INFO_BLOCK_ROWS, type DocumentLayoutConfig, type LayoutColumn } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { pageWide } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import { documentsApi } from '../api/documents.api'
import { SettingsTabs } from '../components/SettingsTabs'
import { lesbarerFehler } from '../lib/binary-error'
import type { DocumentLayoutRow, LayoutError } from '../types/document.types'
import { bestaetige, frage } from '@/lib/dialoge'
import { useWarnungBeimVerlassen } from '@/lib/speichern'

const eingabe =
  'w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
const karte = 'rounded-lg border border-border bg-card p-4 shadow-sm'
const abschnittTitel = 'text-sm font-semibold uppercase tracking-wide text-muted-foreground'
const SCHRIFTEN = ['Inter', 'Arial', 'Helvetica', 'Verdana', 'Georgia', 'Times New Roman']

type Abschnitt = 'page' | 'type' | 'blocks' | 'table' | 'colors'

function kopie<T>(x: T): T {
  return JSON.parse(JSON.stringify(x)) as T
}

function setzePfad(config: DocumentLayoutConfig, pfad: string, wert: unknown): DocumentLayoutConfig {
  const neu = kopie(config) as unknown as Record<string, unknown>
  const teile = pfad.split('.')
  let knoten = neu
  for (const t of teile.slice(0, -1)) knoten = knoten[t] as Record<string, unknown>
  knoten[teile[teile.length - 1]] = wert
  return neu as unknown as DocumentLayoutConfig
}

function fehlerAus(err: unknown): LayoutError[] {
  const daten = (lesbarerFehler(err) as { response?: { data?: { errors?: LayoutError[] } } })?.response?.data
  return Array.isArray(daten?.errors) ? daten.errors : []
}

export default function DocumentLayoutPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const [layouts, setLayouts] = useState<DocumentLayoutRow[]>([])
  const [aktivId, setAktivId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [config, setConfig] = useState<DocumentLayoutConfig>(kopie(DEFAULT_DOCUMENT_LAYOUT))
  const [gespeichertJson, setGespeichertJson] = useState('')
  const [raster, setRaster] = useState(false)
  const [briefpapier, setBriefpapier] = useState(true)
  const [vorschau, setVorschau] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [pruefung, setPruefung] = useState<LayoutError[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const vorschauUrl = useRef<string | null>(null)

  const waehle = (l: DocumentLayoutRow) => {
    setAktivId(l.id)
    setName(l.name)
    setConfig(kopie(l.config))
    setGespeichertJson(JSON.stringify({ name: l.name, config: l.config }))
    setPruefung([])
  }

  useEffect(() => {
    documentsApi
      .layouts()
      .then((r) => {
        setLayouts(r.data.data)
        const start = r.data.data.find((l) => l.isDefault) ?? r.data.data[0]
        if (start) waehle(start)
      })
      .catch((err) => setFehler(fehlertext(err, t)))
    return () => {
      if (vorschauUrl.current) URL.revokeObjectURL(vorschauUrl.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Öffnen laden
  }, [])

  // Vorschau: entprellt, die letzte Anfrage gewinnt.
  useEffect(() => {
    let abgebrochen = false
    const zeit = setTimeout(async () => {
      setLaedt(true)
      try {
        const { data } = await documentsApi.previewLayout({ config, guides: raster, letterhead: briefpapier })
        if (abgebrochen) return
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        if (vorschauUrl.current) URL.revokeObjectURL(vorschauUrl.current)
        vorschauUrl.current = url
        setVorschau(url)
        setPruefung([])
      } catch (err) {
        if (abgebrochen) return
        const liste = fehlerAus(err)
        setPruefung(liste)
        if (!liste.length) setFehler(fehlertext(lesbarerFehler(err), t))
      } finally {
        if (!abgebrochen) setLaedt(false)
      }
    }, 700)
    return () => {
      abgebrochen = true
      clearTimeout(zeit)
    }
  }, [config, raster, briefpapier, t])

  const fehlerAm = useMemo(() => {
    const m = new Map<string, LayoutError>()
    for (const f of pruefung) m.set(f.feld, f)
    return m
  }, [pruefung])

  const geaendert = JSON.stringify({ name, config }) !== gespeichertJson
  // AP-36 5.2: Ausnahme mit Knopf — ungespeichert warnt der Browser beim Schließen.
  useWarnungBeimVerlassen(geaendert)
  const aktiv = layouts.find((l) => l.id === aktivId) ?? null
  const setze = (pfad: string, wert: unknown) => setConfig((c) => setzePfad(c, pfad, wert))
  const zuruecksetzen = (a: Abschnitt) => setConfig((c) => ({ ...c, [a]: kopie(DEFAULT_DOCUMENT_LAYOUT[a]) }))

  const mitMeldung = async (aktion: () => Promise<void>) => {
    setFehler(null)
    setMeldung(null)
    try {
      await aktion()
    } catch (err) {
      const liste = fehlerAus(err)
      if (liste.length) setPruefung(liste)
      setFehler(fehlertext(err, t))
    }
  }

  const speichern = () =>
    mitMeldung(async () => {
      if (!aktiv) return
      const { data } = await documentsApi.updateLayout(aktiv.id, { name, config })
      setLayouts((ls) => ls.map((l) => (l.id === data.data.id ? data.data : l)))
      waehle(data.data)
      setMeldung(t('documents.layoutEditor.saved'))
    })

  const kopieren = () =>
    mitMeldung(async () => {
      const neuerName = await frage(t('documents.layoutEditor.newName'), `${name} (2)`)
      if (!neuerName?.trim()) return
      const { data } = await documentsApi.createLayout({ name: neuerName.trim(), config })
      setLayouts((ls) => [...ls, data.data])
      waehle(data.data)
    })

  const zumStandard = () =>
    mitMeldung(async () => {
      if (!aktiv) return
      const { data } = await documentsApi.updateLayout(aktiv.id, { isDefault: true })
      setLayouts((ls) => ls.map((l) => ({ ...l, isDefault: l.id === data.data.id })))
    })

  const loeschen = () =>
    mitMeldung(async () => {
      if (!aktiv || !(await bestaetige(t('documents.layoutEditor.confirmDelete', { name: aktiv.name })))) return
      await documentsApi.deleteLayout(aktiv.id)
      const rest = layouts.filter((l) => l.id !== aktiv.id)
      setLayouts(rest)
      const start = rest.find((l) => l.isDefault) ?? rest[0]
      if (start) waehle(start)
    })

  /** Ein Zahlenfeld mit Pfad — Meldung und erlaubter Bereich stehen direkt darunter. */
  const Zahl = ({ pfad, schritt = 0.5, beschriftung }: { pfad: string; schritt?: number; beschriftung: string }) => {
    const wert = pfad.split('.').reduce<unknown>((k, s) => (k as Record<string, unknown>)?.[s], config) as number
    const f = fehlerAm.get(pfad)
    return (
      <label className="block space-y-0.5">
        <span className="block text-xs text-muted-foreground">{beschriftung}</span>
        <input
          type="number"
          step={schritt}
          value={Number.isFinite(wert) ? wert : ''}
          onChange={(e) => setze(pfad, e.target.value === '' ? 0 : Number(e.target.value))}
          className={`${eingabe} ${f ? 'border-destructive ring-1 ring-destructive' : ''}`}
        />
        {f && <span className="block text-xs text-destructive">{bereichText(f)}</span>}
      </label>
    )
  }

  const Schalter = ({ pfad, beschriftung }: { pfad: string; beschriftung: string }) => {
    const wert = pfad.split('.').reduce<unknown>((k, s) => (k as Record<string, unknown>)?.[s], config) as boolean
    return (
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!wert} onChange={(e) => setze(pfad, e.target.checked)} />
        {beschriftung}
      </label>
    )
  }

  const bereichText = (f: LayoutError) =>
    `${t(f.meldung)}${f.min !== undefined || f.max !== undefined ? ` (${t('documents.layoutEditor.range', { min: f.min ?? '—', max: f.max ?? '—' })})` : ''}`

  const AbschnittKopf = ({ a }: { a: Abschnitt }) => (
    <div className="mb-3 flex items-center justify-between">
      <h2 className={abschnittTitel}>{t(`documents.layoutEditor.sections.${a}`)}</h2>
      <button type="button" onClick={() => zuruecksetzen(a)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <RotateCcw className="h-3 w-3" />
        {t('documents.layoutEditor.reset')}
      </button>
    </div>
  )

  const fld = (k: string) => t(`documents.layoutEditor.fields.${k}`)

  // ── Tabelle: bekannte Spalten in gespeicherter Reihenfolge, fehlende hinten ──
  const alleSpalten: LayoutColumn[] = [
    ...config.table.columns,
    ...DEFAULT_DOCUMENT_LAYOUT.table.columns.filter((d) => !config.table.columns.some((c) => c.key === d.key)),
  ]
  const spalteAn = (key: string) => config.table.columns.some((c) => c.key === key)
  const setzeSpalten = (spalten: LayoutColumn[]) => setze('table.columns', spalten)
  const schiebe = (key: string, richtung: -1 | 1) => {
    const liste = [...config.table.columns]
    const i = liste.findIndex((c) => c.key === key)
    const j = i + richtung
    if (i < 0 || j < 0 || j >= liste.length) return
    ;[liste[i], liste[j]] = [liste[j], liste[i]]
    setzeSpalten(liste)
  }

  const BLOCK_FELDER: Record<keyof DocumentLayoutConfig['blocks'], string[]> = {
    logo: ['visible', 'x', 'y', 'width'],
    senderLine: ['visible', 'x', 'y', 'width', 'size'],
    addressField: ['visible', 'x', 'y', 'width', 'height', 'addressStartY'],
    infoBlock: ['visible', 'x', 'y', 'width', 'align', 'rows'],
    subject: ['visible', 'y', 'bold'],
    headerText: ['visible'],
    totals: ['align', 'width'],
    paymentBlock: ['visible', 'qr'],
    footerText: ['visible'],
    signature: ['visible', 'text'],
    pageFooter: ['visible', 'y', 'size', 'text'],
    foldMarks: ['visible', 'hole'],
  }

  return (
    <div className={pageWide}>
      <div className="flex items-center gap-3">
        <Link to="/correspondence" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('documents.settings.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('documents.layoutEditor.subtitle')}</p>
        </div>
      </div>
      <SettingsTabs />

      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.layoutEditor.select')}</span>
          <select
            className={`${eingabe} min-w-[14rem] py-2`}
            value={aktivId ?? ''}
            onChange={async (e) => {
              const l = layouts.find((x) => x.id === e.target.value)
              if (l && (!geaendert || (await bestaetige(t('documents.layoutEditor.discard'))))) waehle(l)
            }}
          >
            {layouts.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.isDefault ? ` · ${t('documents.layoutEditor.isDefault')}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('documents.layoutEditor.name')}</span>
          <input className={`${eingabe} py-2`} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </label>
        <button type="button" disabled={!geaendert} onClick={() => void speichern()} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
          <Save className="h-4 w-4" />
          {t('common.save')}
        </button>
        <button type="button" onClick={() => void kopieren()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
          <Copy className="h-4 w-4" />
          {t('documents.layoutEditor.copy')}
        </button>
        {aktiv && !aktiv.isDefault && (
          <>
            <button type="button" onClick={() => void zumStandard()} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
              {t('documents.layoutEditor.makeDefault')}
            </button>
            <button type="button" onClick={() => void loeschen()} className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" />
              {t('common.delete')}
            </button>
          </>
        )}
        {meldung && <span className="text-sm text-emerald-600">✓ {meldung}</span>}
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {pruefung.length > 0 && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <p className="font-medium">{t('documents.layout.invalid')}</p>
          <ul className="mt-1 list-disc pl-5">
            {pruefung.map((f) => (
              <li key={f.feld}>
                <span className="font-mono text-xs">{f.feld}</span>: {bereichText(f)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ── Formular ── */}
        <div className="space-y-4">
          <section className={karte}>
            <AbschnittKopf a="page" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Zahl pfad="page.marginTop" beschriftung={fld('marginTop')} />
              <Zahl pfad="page.marginBottom" beschriftung={fld('marginBottom')} />
              <Zahl pfad="page.marginLeft" beschriftung={fld('marginLeft')} />
              <Zahl pfad="page.marginRight" beschriftung={fld('marginRight')} />
            </div>
          </section>

          <section className={karte}>
            <AbschnittKopf a="type" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <label className="block space-y-0.5">
                <span className="block text-xs text-muted-foreground">{fld('family')}</span>
                <select className={eingabe} value={config.type.family} onChange={(e) => setze('type.family', e.target.value)}>
                  {SCHRIFTEN.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <Zahl pfad="type.sizeBody" beschriftung={fld('sizeBody')} />
              <Zahl pfad="type.sizeTable" beschriftung={fld('sizeTable')} />
              <Zahl pfad="type.sizeSmall" beschriftung={fld('sizeSmall')} />
              <Zahl pfad="type.sizeSubject" beschriftung={fld('sizeSubject')} />
              <Zahl pfad="type.lineHeight" schritt={0.02} beschriftung={fld('lineHeight')} />
            </div>
          </section>

          <section className={karte}>
            <AbschnittKopf a="blocks" />
            <div className="space-y-4">
              {(Object.keys(BLOCK_FELDER) as (keyof DocumentLayoutConfig['blocks'])[]).map((block) => {
                const felder = BLOCK_FELDER[block]
                const f = fehlerAm.get(`blocks.${block}`)
                return (
                  <div key={block} className={`rounded-md border p-3 ${f ? 'border-destructive' : 'border-border/60'}`}>
                    <div className="mb-2 text-sm font-medium">{t(`documents.layoutEditor.blocks.${block}`)}</div>
                    {f && <p className="mb-2 text-xs text-destructive">{bereichText(f)}</p>}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {felder.map((k) => {
                        const pfad = `blocks.${block}.${k}`
                        const wert = (config.blocks[block] as unknown as Record<string, unknown>)[k]
                        if (k === 'visible' || k === 'bold' || k === 'qr' || k === 'hole') return <Schalter key={k} pfad={pfad} beschriftung={fld(k)} />
                        if (k === 'align') {
                          return (
                            <label key={k} className="block space-y-0.5">
                              <span className="block text-xs text-muted-foreground">{fld(k)}</span>
                              <select className={eingabe} value={wert as string} onChange={(e) => setze(pfad, e.target.value)}>
                                <option value="left">{fld('left')}</option>
                                <option value="right">{fld('right')}</option>
                              </select>
                            </label>
                          )
                        }
                        if (k === 'text') {
                          return (
                            <label key={k} className="col-span-2 block space-y-0.5 sm:col-span-4">
                              <span className="block text-xs text-muted-foreground">{fld(block === 'pageFooter' ? 'footerTemplate' : k)}</span>
                              <input className={eingabe} value={wert as string} onChange={(e) => setze(pfad, e.target.value)} />
                            </label>
                          )
                        }
                        if (k === 'rows') {
                          const zeilen = wert as string[]
                          return (
                            <div key={k} className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 sm:col-span-4">
                              {INFO_BLOCK_ROWS.map((r) => (
                                <label key={r} className="flex items-center gap-1.5 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={zeilen.includes(r)}
                                    onChange={(e) => setze(pfad, e.target.checked ? INFO_BLOCK_ROWS.filter((x) => x === r || zeilen.includes(x)) : zeilen.filter((x) => x !== r))}
                                  />
                                  {r === 'project' ? terms.project : t(`documents.layoutEditor.infoRows.${r}`)}
                                </label>
                              ))}
                            </div>
                          )
                        }
                        return <Zahl key={k} pfad={pfad} beschriftung={fld(k)} />
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          <section className={karte}>
            <AbschnittKopf a="table" />
            {fehlerAm.get('table.columns') && <p className="mb-2 text-xs text-destructive">{bereichText(fehlerAm.get('table.columns')!)}</p>}
            <table className="mb-4 w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">{fld('column')}</th>
                  <th className="py-1">{fld('width')}</th>
                  <th className="py-1">{fld('align')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {alleSpalten.map((c) => {
                  const an = spalteAn(c.key)
                  return (
                    <tr key={c.key} className={an ? '' : 'opacity-50'}>
                      <td className="py-1">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={an}
                            disabled={c.key === 'name'}
                            onChange={(e) => setzeSpalten(e.target.checked ? [...config.table.columns, c] : config.table.columns.filter((x) => x.key !== c.key))}
                          />
                          {t(`documents.layoutEditor.columns.${c.key}`)}
                        </label>
                      </td>
                      <td className="py-1 pr-2">
                        {c.width === '*' ? (
                          <span className="text-xs text-muted-foreground">{fld('rest')}</span>
                        ) : (
                          <input
                            type="number"
                            step={0.5}
                            disabled={!an}
                            className={`${eingabe} w-20`}
                            value={c.width}
                            onChange={(e) => setzeSpalten(config.table.columns.map((x) => (x.key === c.key ? { ...x, width: Number(e.target.value) } : x)))}
                          />
                        )}
                      </td>
                      <td className="py-1 pr-2">
                        <select
                          disabled={!an}
                          className={`${eingabe} w-28`}
                          value={c.align}
                          onChange={(e) => setzeSpalten(config.table.columns.map((x) => (x.key === c.key ? { ...x, align: e.target.value as LayoutColumn['align'] } : x)))}
                        >
                          <option value="left">{fld('left')}</option>
                          <option value="center">{fld('center')}</option>
                          <option value="right">{fld('right')}</option>
                        </select>
                      </td>
                      <td className="whitespace-nowrap py-1 text-right">
                        <button type="button" disabled={!an} onClick={() => schiebe(c.key, -1)} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" disabled={!an} onClick={() => schiebe(c.key, 1)} className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Schalter pfad="table.headerRule" beschriftung={fld('headerRule')} />
              <Schalter pfad="table.zebra" beschriftung={fld('zebra')} />
              <Schalter pfad="table.repeatHeader" beschriftung={fld('repeatHeader')} />
              <Schalter pfad="table.carryOver" beschriftung={fld('carryOver')} />
              <Zahl pfad="table.subIndentMm" beschriftung={fld('subIndentMm')} />
              <Schalter pfad="table.lineImages.visible" beschriftung={fld('lineImages')} />
              <Zahl pfad="table.lineImages.maxWidthMm" beschriftung={fld('maxWidthMm')} />
              <Zahl pfad="table.lineImages.maxHeightMm" beschriftung={fld('maxHeightMm')} />
            </div>
          </section>

          <section className={karte}>
            <AbschnittKopf a="colors" />
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={config.colors.accent === null} onChange={(e) => setze('colors.accent', e.target.checked ? null : '#1e40af')} />
                {t('documents.layoutEditor.accentFromBranding')}
              </label>
              {config.colors.accent !== null && (
                <input type="color" value={config.colors.accent} onChange={(e) => setze('colors.accent', e.target.value)} className="h-8 w-14 cursor-pointer rounded border border-border" />
              )}
            </div>
          </section>
        </div>

        {/* ── Vorschau ── */}
        <div className="space-y-2 xl:sticky xl:top-4 xl:self-start">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={raster} onChange={(e) => setRaster(e.target.checked)} />
              {t('documents.layoutEditor.guides')}
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={briefpapier} onChange={(e) => setBriefpapier(e.target.checked)} />
              {t('documents.layoutEditor.letterhead')}
            </label>
            {laedt && <span className="text-xs text-muted-foreground">{t('documents.layoutEditor.previewLoading')}</span>}
          </div>
          {vorschau ? (
            <iframe title={t('documents.layoutEditor.preview')} src={vorschau} className="h-[80vh] w-full rounded-lg border border-border bg-white" />
          ) : (
            <div className="flex h-[60vh] items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
              {t('documents.layoutEditor.previewLoading')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
